import type { emailDraftReviewToolDef } from '@newfront-insurance/ai-api';
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { InferToolInput, ToolUIPart } from 'ai';
import { Check, ClipboardList } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useCopyToClipboard } from 'react-use';

import { MessageResponse } from '../../../ai-elements';
import { getCountBucket, getLengthBucket, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import { copyElementToClipboard, writeTextToClipboard } from '../../lib/rich-clipboard';

type EmailDraftReviewToolInput = InferToolInput<typeof emailDraftReviewToolDef>;
type EmailDraftReviewInput = Omit<Partial<EmailDraftReviewToolInput>, 'body'> & {
  body?: string;
};
type EmailRecipient = EmailDraftReviewToolInput['toRecipients'][number];

interface EmailDraftCardProps {
  draft: EmailDraftReviewInput;
}

type EmailDraftCopyTarget = 'body' | 'subject';

const COPY_FEEDBACK_DURATION_MS = 1600;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function isEmailImportance(value: unknown): value is NonNullable<EmailDraftReviewToolInput['importance']> {
  return value === 'low' || value === 'normal' || value === 'high';
}

function normalizeEmailDraftInput(input: unknown): EmailDraftReviewInput | null {
  if (!isRecord(input)) {
    return null;
  }

  return {
    bccRecipients: Array.isArray(input.bccRecipients) ? (input.bccRecipients as EmailRecipient[]) : undefined,
    body: typeof input.body === 'string' ? input.body : undefined,
    ccRecipients: Array.isArray(input.ccRecipients) ? (input.ccRecipients as EmailRecipient[]) : undefined,
    importance: isEmailImportance(input.importance) ? input.importance : undefined,
    subject: typeof input.subject === 'string' ? input.subject : undefined,
    toRecipients: Array.isArray(input.toRecipients) ? (input.toRecipients as EmailRecipient[]) : undefined,
  };
}

function formatRecipient(recipient: EmailRecipient): string | null {
  const name = recipient.emailAddress?.name?.trim();
  const address = recipient.emailAddress?.address?.trim();

  if (name && address) {
    return `${name} <${address}>`;
  }

  return name || address || null;
}

function formatRecipients(recipients: EmailRecipient[] | undefined): string {
  const formatted = recipients?.map(formatRecipient).filter((recipient): recipient is string => Boolean(recipient));
  return formatted?.length ? formatted.join(', ') : 'None';
}

function EmailHeaderRow({ label, children, action }: { label: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex w-full items-start gap-4 leading-5">
      <div className="text-steel-400 w-14 shrink-0 pt-px text-[12px] font-semibold tracking-[1.2px] uppercase">
        {label}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-start gap-2">
          <div className="min-w-0 text-[14px] font-normal break-words text-[#262D46]">{children}</div>
          {action ? (
            <div className="shrink-0" data-nova-copy-exclude="true">
              {action}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EmailDraftCopyButton({
  ariaLabel,
  className,
  copied,
  onCopy,
  tooltip,
}: {
  ariaLabel: string;
  className?: string;
  copied: boolean;
  onCopy: () => void;
  tooltip: string;
}) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={ariaLabel}
            className={cn('text-steel-400 hover:text-steel-500 h-6 w-6 p-0', className)}
            onClick={onCopy}
            size="small"
            type="button"
            variant="transparent"
          >
            {copied ? (
              <Check aria-label="Copied" color="currentColor" size={18} />
            ) : (
              <ClipboardList aria-label="Copy to clipboard" color="currentColor" size={18} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? 'Copied' : tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function EmailDraftBody({ bodyText }: { bodyText: string }) {
  if (!bodyText) {
    return <span className="text-steel-400 text-[14px] leading-6">(No body)</span>;
  }

  return (
    <MessageResponse
      className={cn(
        'text-[14px] leading-6 text-[#262D46]',
        '[&_p]:my-3 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
        '[&_table]:text-[14px]',
      )}
    >
      {bodyText}
    </MessageResponse>
  );
}

export function EmailDraftCard({ draft }: EmailDraftCardProps) {
  const analytics = useAnalytics();
  const [, copyToClipboard] = useCopyToClipboard();
  const draftBodyContentRef = useRef<HTMLDivElement | null>(null);
  const [copiedTarget, setCopiedTarget] = useState<EmailDraftCopyTarget | null>(null);
  const bodyText = draft.body?.trim() ?? '';
  const hasCc = Boolean(draft.ccRecipients?.length);
  const hasBcc = Boolean(draft.bccRecipients?.length);
  const subject = draft.subject?.trim() || '(no subject)';

  useEffect(() => {
    if (!copiedTarget) {
      return;
    }

    const timer = window.setTimeout(() => setCopiedTarget(null), COPY_FEEDBACK_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [copiedTarget]);

  const trackCopy = (copyTarget: EmailDraftCopyTarget) => {
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.EMAIL_DRAFT_COPY_BUTTON_CLICKED, {
      bodyLengthBucket: getLengthBucket(bodyText.length),
      bccRecipientCountBucket: getCountBucket(draft.bccRecipients?.length),
      ccRecipientCountBucket: getCountBucket(draft.ccRecipients?.length),
      copyTarget,
      subjectLengthBucket: getLengthBucket(subject.length),
      toRecipientCountBucket: getCountBucket(draft.toRecipients?.length),
    });
    setCopiedTarget(copyTarget);
  };

  const copyPlainText = async (text: string) => {
    try {
      await writeTextToClipboard(text);
    } catch {
      copyToClipboard(text);
    }
  };

  const handleCopyBody = async () => {
    const copiedRichContent = copyElementToClipboard(draftBodyContentRef.current);

    if (!copiedRichContent) {
      await copyPlainText(bodyText);
    }

    trackCopy('body');
  };

  const handleCopySubject = async () => {
    await copyPlainText(subject);
    trackCopy('subject');
  };

  return (
    <div
      className={cn(
        "w-full max-w-[720px] overflow-hidden rounded-[16px] font-['Open_Sans',sans-serif]",
        'border border-[#DEE2E7] bg-white',
        'text-[#262D46]',
      )}
      data-testid="email-draft-review-card"
    >
      <div>
        <div className="flex flex-col gap-[10px] px-4 py-[10px]">
          <EmailHeaderRow label="To">{formatRecipients(draft.toRecipients)}</EmailHeaderRow>
          {hasCc && <EmailHeaderRow label="Cc">{formatRecipients(draft.ccRecipients)}</EmailHeaderRow>}
          {hasBcc && <EmailHeaderRow label="Bcc">{formatRecipients(draft.bccRecipients)}</EmailHeaderRow>}
          <div className="h-px w-full bg-[#DEE2E7]" />
          <EmailHeaderRow
            action={
              <EmailDraftCopyButton
                ariaLabel={copiedTarget === 'subject' ? 'Email subject copied' : 'Copy email subject'}
                className="-my-1"
                copied={copiedTarget === 'subject'}
                onCopy={() => void handleCopySubject()}
                tooltip="Copy subject"
              />
            }
            label="Subject"
          >
            {subject}
          </EmailHeaderRow>
        </div>
        <div className="h-px w-full bg-[#DEE2E7]" />
        <div className="px-4 py-3">
          <div ref={draftBodyContentRef}>
            <EmailDraftBody bodyText={bodyText} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-4 pb-3">
        <EmailDraftCopyButton
          ariaLabel={copiedTarget === 'body' ? 'Email body copied' : 'Copy email body'}
          copied={copiedTarget === 'body'}
          onCopy={() => void handleCopyBody()}
          tooltip="Copy body"
        />
      </div>
    </div>
  );
}

export function EmailDraftReviewTool({ part }: { part: ToolUIPart; messageUuid: string }) {
  if (!('input' in part)) {
    return null;
  }

  const draft = normalizeEmailDraftInput(part.input);
  if (!draft) {
    return null;
  }

  return <EmailDraftCard draft={draft} />;
}
